import termsOfServiceRaw from '../../docs/legal/TERMS_OF_SERVICE.md?raw';
import privacyPolicyRaw from '../../docs/legal/PRIVACY_POLICY.md?raw';
import eulaRaw from '../../docs/legal/END_USER_LICENSE_AGREEMENT.md?raw';
import acceptableUsePolicyRaw from '../../docs/legal/ACCEPTABLE_USE_POLICY.md?raw';
import communityGuidelinesRaw from '../../docs/legal/COMMUNITY_GUIDELINES.md?raw';
import contentModerationPolicyRaw from '../../docs/legal/CONTENT_MODERATION_POLICY.md?raw';
import dataRetentionPolicyRaw from '../../docs/legal/DATA_RETENTION_POLICY.md?raw';
import cookiePolicyRaw from '../../docs/legal/COOKIE_POLICY.md?raw';
import dmcaPolicyRaw from '../../docs/legal/DMCA_COPYRIGHT_POLICY.md?raw';

export type LegalSlug =
  | 'terms-of-service'
  | 'privacy-policy'
  | 'eula'
  | 'acceptable-use-policy'
  | 'community-guidelines'
  | 'content-moderation-policy'
  | 'data-retention-policy'
  | 'cookie-policy'
  | 'dmca-policy';

export interface LegalDocument {
  slug: LegalSlug;
  title: string;
  shortDescription: string;
  content: string;
}

/**
 * Shared "Last Updated" date across all policy documents (see docs/legal/FILL_IN_WORKSHEET.md).
 * Bump this whenever any of the underlying documents materially change.
 */
export const LEGAL_VERSION = '2026-04-21';

export const LEGAL_DOCUMENTS: LegalDocument[] = [
  {
    slug: 'terms-of-service',
    title: 'Terms of Service',
    shortDescription: 'The core agreement governing your use of the Platform.',
    content: termsOfServiceRaw,
  },
  {
    slug: 'privacy-policy',
    title: 'Privacy Policy',
    shortDescription: 'What we collect, how we use it, and your privacy rights.',
    content: privacyPolicyRaw,
  },
  {
    slug: 'eula',
    title: 'End User License Agreement',
    shortDescription: 'The software license for using the Application.',
    content: eulaRaw,
  },
  {
    slug: 'acceptable-use-policy',
    title: 'Acceptable Use Policy',
    shortDescription: 'What responsible, healthy use of the Platform looks like.',
    content: acceptableUsePolicyRaw,
  },
  {
    slug: 'community-guidelines',
    title: 'Community Guidelines',
    shortDescription: 'Our values and how we treat each other.',
    content: communityGuidelinesRaw,
  },
  {
    slug: 'content-moderation-policy',
    title: 'Content Moderation Policy',
    shortDescription: 'How content is moderated, reported, and enforced.',
    content: contentModerationPolicyRaw,
  },
  {
    slug: 'data-retention-policy',
    title: 'Data Retention and Deletion Policy',
    shortDescription: 'How long we keep your data and how to request deletion.',
    content: dataRetentionPolicyRaw,
  },
  {
    slug: 'cookie-policy',
    title: 'Cookie and Tracking Policy',
    shortDescription: 'Cookies and similar technologies we use, and your choices.',
    content: cookiePolicyRaw,
  },
  {
    slug: 'dmca-policy',
    title: 'DMCA and Intellectual Property Policy',
    shortDescription: 'Copyright infringement reporting and counter-notification.',
    content: dmcaPolicyRaw,
  },
];

export const LEGAL_DOCUMENTS_BY_SLUG: Record<LegalSlug, LegalDocument> = LEGAL_DOCUMENTS.reduce(
  (acc, doc) => {
    acc[doc.slug] = doc;
    return acc;
  },
  {} as Record<LegalSlug, LegalDocument>
);

/**
 * Maps the bare markdown filenames used in cross-document links (e.g.
 * "[Privacy Policy](PRIVACY_POLICY.md)") to their in-app slug, so links between
 * policy documents can be rewritten to in-app navigation instead of raw .md links.
 */
export const LEGAL_FILENAME_TO_SLUG: Record<string, LegalSlug> = {
  'TERMS_OF_SERVICE.md': 'terms-of-service',
  'PRIVACY_POLICY.md': 'privacy-policy',
  'END_USER_LICENSE_AGREEMENT.md': 'eula',
  'ACCEPTABLE_USE_POLICY.md': 'acceptable-use-policy',
  'COMMUNITY_GUIDELINES.md': 'community-guidelines',
  'CONTENT_MODERATION_POLICY.md': 'content-moderation-policy',
  'DATA_RETENTION_POLICY.md': 'data-retention-policy',
  'COOKIE_POLICY.md': 'cookie-policy',
  'DMCA_COPYRIGHT_POLICY.md': 'dmca-policy',
};

export function resolveLegalSlugFromHref(href: string): LegalSlug | null {
  const filename = href.split('/').pop()?.split('#')[0];
  if (!filename) return null;
  return LEGAL_FILENAME_TO_SLUG[filename] ?? null;
}
